import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaCreditCard, FaChartBar, FaInfoCircle, FaQuestionCircle, FaCheckCircle, FaLightbulb, FaQuestion } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const CreditScoreEstimator = () => {
  const [formData, setFormData] = useState({
    paymentHistory: '',
    creditUtilization: '',
    lengthOfCreditHistory: '',
    newCredit: '',
    creditMix: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const calculateCreditScore = () => {
    const { paymentHistory, creditUtilization, lengthOfCreditHistory, newCredit, creditMix } = formData;

    if (!paymentHistory || !creditUtilization || !lengthOfCreditHistory || !newCredit || !creditMix) {
      setError('Please fill in all required fields.');
      return;
    }

    const totalScore = (
      parseFloat(paymentHistory) +
      parseFloat(creditUtilization) +
      parseFloat(lengthOfCreditHistory) +
      parseFloat(newCredit) +
      parseFloat(creditMix)
    );

    setResults({ totalScore: totalScore.toFixed(0) });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatScore = (value) => {
    return `${value}`;
  };

  return (
    <div className="calculator-container bg-gradient-to-r from-blue-500 to-cyan-500 p-4 text-white">
      <Helmet>
        <title>Credit Score Estimator</title>
        <meta name="description" content="Estimate your credit score with our easy-to-use calculator." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaCreditCard className="calculator-icon text-4xl text-white" />
        <h1 className="text-3xl font-bold text-black mb-2">Credit Score Estimator</h1>
        <p className="text-gray-300">Estimate your credit score based on key factors</p>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={(e) => e.preventDefault()} className="calculator-form space-y-6">
            <div className="input-group">
              <label htmlFor="paymentHistory" className="block text-gray-700 font-bold mb-2">
                <FaCreditCard className="inline-block mr-2 text-gray-700" /> Payment History (0-35)
              </label>
              <input
                type="number"
                id="paymentHistory"
                name="paymentHistory"
                value={formData.paymentHistory}
                onChange={handleChange}
                placeholder="Enter payment history score"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Score based on your payment history" className="text-gray-700 hover:text-blue-500 transition-colors duration-200" />
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="creditUtilization" className="block text-gray-700 font-bold mb-2">
                <FaCreditCard className="inline-block mr-2 text-gray-700" /> Credit Utilization (0-30)
              </label>
              <input
                type="number"
                id="creditUtilization"
                name="creditUtilization"
                value={formData.creditUtilization}
                onChange={handleChange}
                placeholder="Enter credit utilization score"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Score based on your credit utilization" className="text-gray-700 hover:text-blue-500 transition-colors duration-200" />
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="lengthOfCreditHistory" className="block text-gray-700 font-bold mb-2">
                <FaCreditCard className="inline-block mr-2 text-gray-700" /> Length of Credit History (0-15)
              </label>
              <input
                type="number"
                id="lengthOfCreditHistory"
                name="lengthOfCreditHistory"
                value={formData.lengthOfCreditHistory}
                onChange={handleChange}
                placeholder="Enter length of credit history score"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Score based on your length of credit history" className="text-gray-700 hover:text-blue-500 transition-colors duration-200" />
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="newCredit" className="block text-gray-700 font-bold mb-2">
                <FaCreditCard className="inline-block mr-2 text-gray-700" /> New Credit (0-10)
              </label>
              <input
                type="number"
                id="newCredit"
                name="newCredit"
                value={formData.newCredit}
                onChange={handleChange}
                placeholder="Enter new credit score"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Score based on your new credit" className="text-gray-700 hover:text-blue-500 transition-colors duration-200" />
              </span>
            </div>

            <div className="input-group">
              <label htmlFor="creditMix" className="block text-gray-700 font-bold mb-2">
                <FaCreditCard className="inline-block mr-2 text-gray-700" /> Credit Mix (0-10)
              </label>
              <input
                type="number"
                id="creditMix"
                name="creditMix"
                value={formData.creditMix}
                onChange={handleChange}
                placeholder="Enter credit mix score"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
              <span className="info-icon">
                <FaInfoCircle title="Score based on your credit mix" className="text-gray-700 hover:text-blue-500 transition-colors duration-200" />
              </span>
            </div>

            <button
              onClick={calculateCreditScore}
              className="calculate-button w-full bg-blue-500 text-white rounded-md py-3 px-6 font-semibold hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200"
            >
              Estimate Credit Score
            </button>

            {error && (
              <div className="error-message bg-red-50 text-red-600 rounded-md p-3 mt-4">
                {error}
              </div>
            )}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Results</h2>
              <FaChartBar className="results-icon text-3xl text-gray-900" />
            </div>
            
            <div className="results-chart bg-white bg-opacity-90 rounded-lg p-6">
              <Chart
                options={{
                  chart: {
                    type: 'bar',
                    height: 350,
                    toolbar: {
                      show: false
                    },
                    animations: {
                      enabled: true,
                      easing: 'easeinout',
                      speed: 800
                    }
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 4,
                      horizontal: true
                    }
                  },
                  dataLabels: {
                    enabled: false
                  },
                  xaxis: {
                    categories: ['Payment History', 'Credit Utilization', 'Length of Credit History', 'New Credit', 'Credit Mix']
                  },
                  yaxis: {
                    labels: {
                      formatter: function (value) {
                        return value;
                      }
                    }
                  },
                  fill: {
                    opacity: 1
                  },
                  colors: ['#4F46E5', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6']
                }}
                series={[
                  {
                    name: 'Score',
                    data: [
                      parseFloat(formData.paymentHistory) || 0,
                      parseFloat(formData.creditUtilization) || 0,
                      parseFloat(formData.lengthOfCreditHistory) || 0,
                      parseFloat(formData.newCredit) || 0,
                      parseFloat(formData.creditMix) || 0
                    ]
                  }
                ]}
                type="bar"
                height={350}
              />
            </div>

            <div className="results-details mt-6 bg-green-500 bg-opacity-90 rounded-lg p-6">
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaCreditCard className="mr-3 text-gray-900" /> Estimated Credit Score
                </div>
                <div className="text-gray-900 font-semibold">
                  {formatScore(parseFloat(results.totalScore))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaLightbulb className="feature-icon text-3xl text-yellow-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Comprehensive Estimation</h3>
            <p className="text-gray-700">Estimate your credit score based on key factors.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaChartBar className="feature-icon text-3xl text-green-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Visual Representation</h3>
            <p className="text-gray-700">See a bar chart representation of your credit score components.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaCheckCircle className="feature-icon text-3xl text-blue-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Financial Insight</h3>
            <p className="text-gray-700">Gain insight into your credit health with a clear score estimation.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5 space-y-3">
          <li className="text-gray-700">Enter your payment history score (0-35)</li>
          <li className="text-gray-700">Enter your credit utilization score (0-30)</li>
          <li className="text-gray-700">Enter your length of credit history score (0-15)</li>
          <li className="text-gray-700">Enter your new credit score (0-10)</li>
          <li className="text-gray-700">Enter your credit mix score (0-10)</li>
          <li className="text-gray-700">Click "Estimate Credit Score" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is a credit score?</h3>
            <p className="text-gray-700">A credit score is a numerical representation of your creditworthiness.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Why is a credit score important?</h3>
            <p className="text-gray-700">A credit score helps lenders assess your credit risk and determines your eligibility for loans and credit cards.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">How can I improve my credit score?</h3>
            <p className="text-gray-700">You can improve your credit score by making timely payments, reducing credit utilization, and maintaining a healthy credit mix.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaQuestion className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What factors affect my credit score?</h3>
            <p className="text-gray-700">Factors include payment history, credit utilization, length of credit history, new credit, and credit mix.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditScoreEstimator;